<template>
  <LoadingCard :loading="loading" class="overflow-visible">
    <!-- Header -->
    <div class="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center gap-3 min-w-0">
        <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
          <svg class="h-4.5 w-4.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.25 4.533A9.707 9.707 0 006 3a9.735 9.735 0 00-3.25.555.75.75 0 00-.5.707v14.25a.75.75 0 001 .707A8.237 8.237 0 016 18.75c1.995 0 3.823.707 5.25 1.886V4.533zM12.75 20.636A8.214 8.214 0 0118 18.75c.966 0 1.89.166 2.75.47a.75.75 0 001-.708V4.262a.75.75 0 00-.5-.707A9.735 9.735 0 0018 3a9.707 9.707 0 00-5.25 1.533v16.103z" />
          </svg>
        </div>
        <div class="min-w-0">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">دوره‌های در انتظار تأیید</h3>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">بررسی ساختار دوره، فصل‌ها و درس‌ها</p>
        </div>
      </div>
      <div class="flex items-center gap-1.5 shrink-0">
        <span
          v-if="courses.length > 0"
          class="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-primary-50 dark:bg-primary-900/30 px-2 text-xs font-semibold text-primary-700 dark:text-primary-400"
        >
          {{ courses.length }}
        </span>
        <button
          @click="fetch"
          :disabled="loading"
          class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:text-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          title="بارگذاری مجدد"
        >
          <svg :class="['h-4 w-4', loading ? 'animate-spin' : '']" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Empty -->
    <div v-if="!loading && courses.length === 0" class="flex flex-col items-center justify-center py-12 px-6 text-center">
      <p class="text-sm font-medium text-gray-600 dark:text-gray-300">هیچ دوره‌ای در انتظار تأیید نیست</p>
    </div>

    <!-- Course list -->
    <div v-else-if="courses.length > 0" class="divide-y divide-gray-100 dark:divide-gray-700/60">
      <div
        v-for="course in courses"
        :key="course.id"
        @click="openModal(course)"
        class="group flex items-center gap-3 px-4 sm:px-6 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
      >
        <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-500">
          {{ initial(course.title) }}
        </div>
        <div class="flex-1 min-w-0">
          <p class="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{{ course.title }}</p>
          <p v-if="course.institute" class="text-xs text-gray-400 truncate">{{ course.institute.name }}</p>
        </div>
        <StatusBadge tone="primary" class="shrink-0">{{ course.pending_count }} مورد</StatusBadge>
      </div>
    </div>

    <!-- Modal -->
    <Modal v-if="modalMounted" :show="modalMounted" size="5xl" @close-via-escape="onMainModalEscape">
      <Transition
        appear
        name="approval-modal"
        @after-leave="finalizeModalClose"
      >
        <div v-if="modalVisible" ref="modalShell" class="approval-modal-shell">
          <div ref="modalPanel" class="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg max-h-[95vh] flex flex-col">
        <div class="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700 shrink-0">
          <div class="min-w-0">
            <h2 class="text-sm sm:text-base font-bold text-gray-900 dark:text-gray-100 truncate">
              {{ detail?.course?.title ?? selected.title }}
            </h2>
            <p v-if="detail?.course?.institute" class="text-xs text-gray-400 truncate mt-0.5">
              {{ detail.course.institute.name }}
            </p>
          </div>
          <button @click="closeModal" class="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <!-- Tabs -->
        <div v-if="detail && !detailLoading" class="shrink-0 border-b border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/80 px-4 sm:px-6">
          <div class="flex gap-1 overflow-x-auto">
            <button
              type="button"
              class="approval-tab"
              :class="{ 'approval-tab-active': activeTab === 'course' }"
              @click="activeTab = 'course'"
            >
              اطلاعات دوره
              <span v-if="detail.course.has_pending" class="approval-tab-dot" />
            </button>
            <button
              type="button"
              class="approval-tab"
              :class="{ 'approval-tab-active': activeTab === 'curriculum' }"
              @click="activeTab = 'curriculum'"
            >
              فصل‌ها و درس‌ها
              <span v-if="curriculumPendingCount > 0" class="approval-tab-badge">{{ curriculumPendingCount }}</span>
            </button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto p-4 sm:p-6">
          <div v-if="detailLoading" class="flex justify-center py-16 text-gray-400">
            <svg class="h-6 w-6 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          </div>

          <template v-else-if="detail">
            <Transition name="tab-fade" mode="out-in">
              <!-- Tab: Course info -->
              <section v-if="activeTab === 'course'" key="tab-course" class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div class="flex items-center gap-2 bg-gray-50 dark:bg-gray-700/40 px-3 sm:px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                <span class="tree-icon tree-icon-course">د</span>
                <h3 class="text-sm font-bold text-gray-900 dark:text-gray-100">اطلاعات دوره</h3>
                <StatusBadge v-if="detail.course.has_pending" tone="warning">در انتظار</StatusBadge>
              </div>
              <div class="p-3 sm:p-4 space-y-3">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <InfoField label="عنوان" :value="detail.course.title" />
                  <InfoField label="نوع" :value="detail.course.type_label" />
                  <InfoField label="دسته‌بندی" :value="detail.course.category_name" />
                  <InfoField label="سطح" :value="detail.course.level_label" />
                  <InfoField label="قیمت" :value="formatPrice(detail.course.price)" />
                  <InfoField label="تخفیف" :value="detail.course.discount_percent != null ? `${detail.course.discount_percent}%` : null" />
                  <InfoField label="پیش‌نیاز" :value="detail.course.has_prerequisite ? 'دارد' : 'ندارد'" />
                  <InfoField label="منتورینگ" :value="detail.course.is_mentored ? 'دارد' : 'ندارد'" />
                </div>
                <InfoField label="توضیحات" :value="detail.course.description" />
                <InfoField label="دید کلی" :value="detail.course.overview" />
                <ApprovalDiffPanel
                  v-if="detail.course.approval"
                  :approval="detail.course.approval"
                  :rejecting-id="rejectingId"
                  :reject-reason="rejectReason"
                  :action-loading="actionLoading"
                  @approve="handleApprove"
                  @start-reject="startReject"
                  @cancel-reject="cancelReject"
                  @reject="handleReject"
                  @update:reject-reason="rejectReason = $event"
                />
              </div>
              </section>

              <!-- Tab: Chapters & lessons -->
              <section v-else key="tab-curriculum">
              <div class="flex items-center justify-between mb-3 px-1">
                <h3 class="text-sm font-bold text-gray-900 dark:text-gray-100">فصل‌ها و درس‌ها</h3>
                <span class="text-xs text-gray-400">{{ detail.chapters.length }} فصل</span>
              </div>

              <div v-if="detail.chapters.length === 0" class="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400">
                فصلی ثبت نشده
              </div>

              <div class="space-y-3">
                <div
                  v-for="chapter in detail.chapters"
                  :key="chapter.id"
                  class="course-tree-chapter rounded-xl border overflow-hidden"
                  :class="chapter.has_pending ? 'border-amber-300 dark:border-amber-700' : 'border-gray-200 dark:border-gray-700'"
                >
                  <!-- Chapter node -->
                  <button
                    type="button"
                    class="w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 text-right transition-colors duration-200"
                    :class="openChapters.includes(chapter.id) ? 'bg-primary/5 dark:bg-primary-900/20' : 'bg-white dark:bg-gray-800'"
                    @click="toggleChapter(chapter.id)"
                  >
                    <span class="tree-icon tree-icon-chapter shrink-0">ف</span>
                    <div class="flex-1 min-w-0 text-right">
                      <div class="flex flex-wrap items-center gap-1.5">
                        <StatusBadge v-if="chapter.approval" tone="warning">{{ chapter.approval.type_label }}</StatusBadge>
                        <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ chapter.title }}</span>
                        <span class="text-xs text-gray-400">({{ chapter.lessons_count }} درس)</span>
                      </div>
                    </div>
                    <svg
                      class="h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ease-out"
                      :class="openChapters.includes(chapter.id) ? 'rotate-180' : ''"
                      viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                    ><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
                  </button>

                  <Transition
                    name="accordion-expand"
                    @enter="onAccordionEnter"
                    @after-enter="onAccordionAfterEnter"
                    @leave="onAccordionLeave"
                  >
                    <div v-if="openChapters.includes(chapter.id)" class="border-t border-gray-200 dark:border-gray-700 overflow-hidden">
                    <!-- Chapter approval -->
                    <div v-if="chapter.approval" class="p-3 sm:p-4 bg-amber-50/50 dark:bg-amber-900/10 border-b border-amber-100 dark:border-amber-900/30">
                      <p class="text-xs font-bold text-amber-700 dark:text-amber-400 mb-2">تغییرات فصل</p>
                      <ApprovalDiffPanel
                        :approval="chapter.approval"
                        :can-approve="chapter.can_approve"
                        :blocked-reason="chapter.blocked_reason"
                        :rejecting-id="rejectingId"
                        :reject-reason="rejectReason"
                        :action-loading="actionLoading"
                        @approve="handleApprove"
                        @start-reject="startReject"
                        @cancel-reject="cancelReject"
                        @reject="handleReject"
                        @update:reject-reason="rejectReason = $event"
                      />
                    </div>

                    <!-- Bulk approve lessons -->
                    <div
                      v-if="chapter.pending_lessons_count > 0"
                      class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 sm:px-4 py-2.5 bg-gray-50 dark:bg-gray-700/30 border-b border-gray-100 dark:border-gray-700"
                    >
                      <span class="text-xs text-gray-500">
                        {{ chapter.pending_lessons_count }} درس در انتظار تأیید
                      </span>
                      <button
                        v-if="chapter.can_approve_lessons"
                        type="button"
                        class="w-full sm:w-auto rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-60 px-3 py-1.5 text-xs font-semibold text-white transition-colors"
                        :disabled="actionLoading"
                        @click="handleApproveAllLessons(chapter)"
                      >
                        تأیید کل درس‌های این فصل
                      </button>
                      <p v-else-if="chapter.lessons_blocked_reason" class="text-xs text-amber-600 dark:text-amber-400">
                        {{ chapter.lessons_blocked_reason }}
                      </p>
                    </div>

                    <!-- Lessons tree -->
                    <div v-if="chapter.lessons.length === 0" class="tree-lessons-empty py-4 text-center text-xs text-gray-400">
                      درسی ثبت نشده
                    </div>

                    <ul v-else class="tree-lessons list-none m-0 p-0 pr-4 sm:pr-6">
                      <li
                        v-for="lesson in chapter.lessons"
                        :key="lesson.id"
                        class="tree-lesson-item"
                        :class="{ 'tree-lesson-pending': lesson.approval }"
                      >
                        <button
                          type="button"
                          class="tree-lesson-row w-full flex items-center gap-2 text-right transition-colors duration-200"
                          :class="lesson.approval ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/20' : 'cursor-default'"
                          @click="lesson.approval && toggleLesson(lesson.id)"
                        >
                          <span class="tree-icon tree-icon-lesson shrink-0">د</span>
                          <div class="flex-1 min-w-0 py-2.5">
                            <div class="flex flex-wrap items-center gap-1.5">
                              <span
                                v-if="lesson.approval"
                                class="inline-block h-2 w-2 rounded-full bg-amber-400 shrink-0"
                              />
                              <span class="text-sm text-gray-800 dark:text-gray-200">{{ lesson.title }}</span>
                              <span v-if="lesson.type_label" class="text-[11px] text-gray-400">{{ lesson.type_label }}</span>
                              <StatusBadge v-if="lesson.approval" tone="warning">{{ lesson.approval.type_label }}</StatusBadge>
                            </div>
                          </div>
                          <svg
                            v-if="lesson.approval"
                            class="h-3.5 w-3.5 shrink-0 text-gray-400 ml-2 transition-transform duration-200 ease-out"
                            :class="openLessons.includes(lesson.id) ? 'rotate-180' : ''"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                          >
                            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        <Transition
                          name="accordion-expand"
                          @enter="onAccordionEnter"
                          @after-enter="onAccordionAfterEnter"
                          @leave="onAccordionLeave"
                        >
                          <div
                            v-if="lesson.approval && openLessons.includes(lesson.id)"
                            class="tree-lesson-detail mr-6 sm:mr-8 mb-3 ml-1 p-3 rounded-lg bg-amber-50/40 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 overflow-hidden"
                          >
                            <ApprovalDiffPanel
                              :approval="lesson.approval"
                              :can-approve="lesson.can_approve"
                              :blocked-reason="lesson.blocked_reason"
                              :rejecting-id="rejectingId"
                              :reject-reason="rejectReason"
                              :action-loading="actionLoading"
                              @approve="handleApprove"
                              @start-reject="startReject"
                              @cancel-reject="cancelReject"
                              @reject="handleReject"
                              @update:reject-reason="rejectReason = $event"
                            />
                          </div>
                        </Transition>
                      </li>
                    </ul>
                    </div>
                  </Transition>
                </div>
              </div>
              </section>
            </Transition>
          </template>
        </div>

        <div class="shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 sm:px-6 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div class="text-sm min-h-[1.25rem]">
            <span v-if="actionSuccess" class="text-green-600 dark:text-green-400">{{ actionSuccess }}</span>
            <span v-if="actionError" class="text-red-600 dark:text-red-400">{{ actionError }}</span>
          </div>
          <button @click="closeModal" class="w-full sm:w-auto rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200">
            بستن
          </button>
        </div>
          </div>
        </div>
      </Transition>
    </Modal>

    <!-- Confirm dialog — stacked above main modal with its own backdrop -->
    <Teleport to="body">
      <div v-if="confirmMounted" class="approval-confirm-root">
        <Transition
          appear
          name="approval-confirm-fade"
          @after-leave="finalizeConfirmClose"
        >
          <div v-if="confirmVisible" class="approval-confirm-layer">
            <div
              class="approval-confirm-backdrop"
              aria-hidden="true"
              @click="closeConfirmDialog"
            />
            <div
              class="approval-confirm-panel bg-white dark:bg-gray-800 rounded-xl p-6 shadow-2xl"
              role="dialog"
              aria-modal="true"
              :aria-labelledby="confirmTitleId"
              @click.stop
            >
              <h3 :id="confirmTitleId" class="text-base font-bold text-center mb-2 text-gray-900 dark:text-gray-100">
                {{ confirmDialog.title }}
              </h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">{{ confirmDialog.message }}</p>
              <div class="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  class="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200"
                  @click="closeConfirmDialog"
                >
                  انصراف
                </button>
                <button
                  type="button"
                  class="flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white"
                  :class="confirmDialog.tone === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'"
                  @click="runConfirmedAction"
                >
                  تأیید
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Teleport>
  </LoadingCard>
</template>

<script>
import ApprovalDiffPanel from './ApprovalDiffPanel.vue'

const StatusBadge = {
  props: { tone: String },
  computed: {
    classes() {
      const tones = {
        gray: 'bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300',
        primary: 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
        warning: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
      }
      return tones[this.tone] ?? tones.gray
    },
  },
  render() {
    return window.Vue.h('span', {
      class: `inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${this.classes}`,
    }, this.$slots.default ? this.$slots.default() : '')
  },
}

const InfoField = {
  props: { label: String, value: [String, Number] },
  render() {
    return window.Vue.h('div', {}, [
      window.Vue.h('p', { class: 'text-xs text-gray-500 mb-0.5' }, this.label),
      window.Vue.h('p', { class: 'font-medium text-gray-800 dark:text-gray-200 break-words' }, this.value ?? '—'),
    ])
  },
}

export default {
  name: 'PendingCourseApprovalCard',
  components: { StatusBadge, InfoField, ApprovalDiffPanel },

  props: {
    card: { type: Object, required: true },
  },

  data: () => ({
    loading: true,
    courses: [],
    selected: null,
    modalMounted: false,
    modalVisible: false,
    modalResizeObserver: null,
    confirmMounted: false,
    confirmVisible: false,
    detail: null,
    detailLoading: false,
    activeTab: 'course',
    openChapters: [],
    openLessons: [],
    actionLoading: false,
    actionSuccess: '',
    actionError: '',
    rejectingId: null,
    rejectReason: '',
    confirmDialog: { show: false, title: '', message: '', tone: 'success', action: null },
    confirmTitleId: `approval-confirm-title-${Math.random().toString(36).slice(2, 9)}`,
  }),

  computed: {
    endpoint() {
      return this.card?.apiEndpoint ?? '/dashboard-api/approval/courses'
    },

    curriculumPendingCount() {
      if (!this.detail?.chapters) return 0
      return this.detail.chapters.reduce((sum, ch) => {
        let count = ch.approval ? 1 : 0
        count += (ch.lessons ?? []).filter((l) => l.approval).length
        return sum + count
      }, 0)
    },
  },

  watch: {
    activeTab() {
      this.$nextTick(() => this.remeasureModalHeight())
    },

    rejectingId() {
      this.$nextTick(() => this.remeasureModalHeight())
    },
  },

  created() {
    this.fetch()
  },

  beforeUnmount() {
    this.teardownModalHeightObserver()
  },

  methods: {
    initial(name) {
      return (name ?? '').trim().charAt(0) || '?'
    },

    formatPrice(price) {
      if (price == null) return '—'
      return Number(price).toLocaleString('fa-IR') + ' تومان'
    },

    async fetch() {
      this.loading = true
      try {
        const { data } = await Nova.request().get(this.endpoint)
        this.courses = data.courses ?? []
      } catch {
        this.courses = []
      } finally {
        this.loading = false
      }
    },

    async reloadDetail() {
      const courseId = this.selected?.id
      if (!courseId) return

      const { data } = await Nova.request().get(`${this.endpoint}/${courseId}`)
      this.detail = data
      this.openChapters = []
      this.openLessons = []

      const curriculumPending = (data.chapters ?? []).reduce((sum, ch) => {
        let count = ch.approval ? 1 : 0
        count += (ch.lessons ?? []).filter((l) => l.approval).length
        return sum + count
      }, 0)

      this.activeTab = data.course?.has_pending ? 'course' : (curriculumPending > 0 ? 'curriculum' : 'course')
    },

    async openModal(course) {
      this.selected = course
      this.detail = null
      this.detailLoading = true
      this.resetActionState()
      this.activeTab = 'course'
      this.openChapters = []
      this.openLessons = []
      this.modalMounted = true
      this.modalVisible = false

      await this.$nextTick()
      requestAnimationFrame(() => {
        this.modalVisible = true
        this.$nextTick(() => this.setupModalHeightObserver())
      })

      try {
        await this.reloadDetail()
      } catch {
        this.detail = null
      } finally {
        this.detailLoading = false
        this.$nextTick(() => this.remeasureModalHeight())
      }
    },

    closeModal() {
      if (!this.modalMounted || this.confirmMounted) return
      this.teardownModalHeightObserver()
      this.resetActionState()
      this.modalVisible = false
    },

    onMainModalEscape() {
      if (this.confirmMounted) {
        this.closeConfirmDialog()
        return
      }
      this.closeModal()
    },

    finalizeModalClose() {
      this.modalMounted = false
      this.selected = null
      this.detail = null
      this.activeTab = 'course'
      this.openChapters = []
      this.openLessons = []
    },

    setupModalHeightObserver() {
      this.teardownModalHeightObserver()

      const shell = this.$refs.modalShell
      const panel = this.$refs.modalPanel
      if (!shell || !panel) return

      shell.style.overflow = 'hidden'
      shell.style.transition = 'height 0.32s cubic-bezier(0.4, 0, 0.2, 1)'

      const applyHeight = () => {
        shell.style.height = `${panel.getBoundingClientRect().height}px`
      }

      applyHeight()

      this.modalResizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(applyHeight)
      })
      this.modalResizeObserver.observe(panel)
    },

    remeasureModalHeight() {
      const shell = this.$refs.modalShell
      const panel = this.$refs.modalPanel
      if (shell && panel) {
        shell.style.height = `${panel.getBoundingClientRect().height}px`
      }
    },

    teardownModalHeightObserver() {
      if (this.modalResizeObserver) {
        this.modalResizeObserver.disconnect()
        this.modalResizeObserver = null
      }

      const shell = this.$refs.modalShell
      if (shell) {
        shell.style.height = ''
        shell.style.overflow = ''
        shell.style.transition = ''
      }
    },

    toggleChapter(id) {
      const idx = this.openChapters.indexOf(id)
      if (idx >= 0) this.openChapters.splice(idx, 1)
      else this.openChapters.push(id)
    },

    toggleLesson(id) {
      const idx = this.openLessons.indexOf(id)
      if (idx >= 0) this.openLessons.splice(idx, 1)
      else this.openLessons.push(id)
    },

    onAccordionEnter(el) {
      el.style.height = '0'
      el.style.overflow = 'hidden'
      el.style.opacity = '0'
      requestAnimationFrame(() => {
        el.style.transition = 'height 0.28s ease, opacity 0.22s ease'
        el.style.height = `${el.scrollHeight}px`
        el.style.opacity = '1'
      })
    },

    onAccordionAfterEnter(el) {
      el.style.height = ''
      el.style.overflow = ''
      el.style.transition = ''
      el.style.opacity = ''
    },

    onAccordionLeave(el) {
      el.style.height = `${el.scrollHeight}px`
      el.style.overflow = 'hidden'
      el.style.opacity = '1'
      requestAnimationFrame(() => {
        el.style.transition = 'height 0.24s ease, opacity 0.2s ease'
        el.style.height = '0'
        el.style.opacity = '0'
      })
    },

    resetActionState() {
      this.rejectingId = null
      this.rejectReason = ''
      this.actionSuccess = ''
      this.actionError = ''
    },

    startReject(approval) {
      this.rejectingId = approval.id
      this.rejectReason = ''
    },

    cancelReject() {
      this.rejectingId = null
      this.rejectReason = ''
    },

    handleApprove(approval) {
      this.openConfirmDialog({
        title: 'تأیید درخواست',
        message: `آیا از تأیید «${approval.model_label}» مطمئن هستید؟`,
        tone: 'success',
        action: () => this.doAction(`${this.endpoint}/requests/${approval.id}/approve`, {}, 'درخواست تأیید شد'),
      })
    },

    handleApproveAllLessons(chapter) {
      this.openConfirmDialog({
        title: 'تأیید همه درس‌ها',
        message: `آیا از تأیید ${chapter.pending_lessons_count} درس فصل «${chapter.title}» مطمئن هستید؟`,
        tone: 'success',
        action: () => this.doAction(
          `${this.endpoint}/chapters/${chapter.id}/approve-lessons`,
          {},
          `${chapter.pending_lessons_count} درس تأیید شد`
        ),
      })
    },

    handleReject(approval) {
      this.openConfirmDialog({
        title: 'رد درخواست',
        message: `آیا از رد «${approval.model_label}» مطمئن هستید؟`,
        tone: 'danger',
        action: () => this.doAction(
          `${this.endpoint}/requests/${approval.id}/reject`,
          { reason: this.rejectReason },
          'درخواست رد شد'
        ).then((ok) => { if (ok) this.cancelReject() }),
      })
    },

    openConfirmDialog({ title, message, tone = 'success', action }) {
      this.confirmDialog = { show: true, title, message, tone, action }
      this.confirmMounted = true
      this.confirmVisible = false
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.confirmVisible = true
        })
      })
    },

    closeConfirmDialog() {
      if (!this.confirmMounted) return
      this.confirmVisible = false
    },

    finalizeConfirmClose() {
      this.confirmMounted = false
      this.confirmDialog = { show: false, title: '', message: '', tone: 'success', action: null }
    },

    async runConfirmedAction() {
      const action = this.confirmDialog.action
      this.closeConfirmDialog()
      if (typeof action === 'function') await action()
    },

    async doAction(url, payload, successMsg) {
      this.actionLoading = true
      this.actionSuccess = ''
      this.actionError = ''
      const courseId = this.selected?.id

      try {
        await Nova.request().post(url, payload)
        this.actionSuccess = successMsg
        await this.fetch()

        if (courseId) {
          await this.reloadDetail()
          if (!this.courses.find((c) => c.id === courseId)) {
            setTimeout(() => { if (this.selected?.id === courseId) this.closeModal() }, 1200)
          }
        }

        return true
      } catch (err) {
        this.actionError = err?.response?.data?.message ?? 'خطایی رخ داد'
        return false
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>

<style scoped>
.approval-modal-shell {
  will-change: height;
}

.approval-modal-enter-active,
.approval-modal-leave-active {
  transition:
    opacity 0.28s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

.approval-modal-enter-from,
.approval-modal-leave-to {
  opacity: 0;
  transform: scale(0.96) translateY(12px);
}

.approval-confirm-root {
  position: fixed;
  inset: 0;
  z-index: 70;
  pointer-events: none;
}

.approval-confirm-layer {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  pointer-events: auto;
}

.approval-confirm-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(17, 24, 39, 0.55);
}

html.dark .approval-confirm-backdrop,
.dark .approval-confirm-backdrop {
  background: rgba(0, 0, 0, 0.65);
}

.approval-confirm-panel {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 24rem;
}

.approval-confirm-fade-enter-active,
.approval-confirm-fade-leave-active {
  transition: opacity 0.24s cubic-bezier(0.4, 0, 0.2, 1);
}

.approval-confirm-fade-enter-active .approval-confirm-backdrop,
.approval-confirm-fade-leave-active .approval-confirm-backdrop {
  transition: opacity 0.24s cubic-bezier(0.4, 0, 0.2, 1);
}

.approval-confirm-fade-enter-active .approval-confirm-panel,
.approval-confirm-fade-leave-active .approval-confirm-panel {
  transition:
    opacity 0.24s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.24s cubic-bezier(0.4, 0, 0.2, 1);
}

.approval-confirm-fade-enter-from,
.approval-confirm-fade-leave-to {
  opacity: 1;
}

.approval-confirm-fade-enter-from .approval-confirm-backdrop,
.approval-confirm-fade-leave-to .approval-confirm-backdrop {
  opacity: 0;
}

.approval-confirm-fade-enter-from .approval-confirm-panel,
.approval-confirm-fade-leave-to .approval-confirm-panel {
  opacity: 0;
  transform: scale(0.95) translateY(10px);
}

.tab-fade-enter-active,
.tab-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.tab-fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.tab-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.approval-tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
  padding: 0.75rem 1rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: rgba(var(--colors-gray-500), 1);
  border-bottom: 2px solid transparent;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.approval-tab:hover {
  color: rgba(var(--colors-gray-700), 1);
}

.approval-tab-active {
  color: rgba(var(--colors-primary-600), 1);
  border-bottom-color: rgba(var(--colors-primary-500), 1);
  font-weight: 600;
}

.approval-tab-dot {
  display: inline-block;
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 9999px;
  background: rgb(251 191 36);
}

.approval-tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.125rem;
  height: 1.125rem;
  padding: 0 0.3rem;
  border-radius: 9999px;
  font-size: 0.625rem;
  font-weight: 700;
  background: rgba(var(--colors-amber-100), 1);
  color: rgba(var(--colors-amber-800), 1);
}

html.dark .approval-tab,
.dark .approval-tab {
  color: rgba(var(--colors-gray-400), 1);
}

html.dark .approval-tab:hover,
.dark .approval-tab:hover {
  color: rgba(var(--colors-gray-200), 1);
}

html.dark .approval-tab-active,
.dark .approval-tab-active {
  color: rgba(var(--colors-primary-400), 1);
}

html.dark .approval-tab-badge,
.dark .approval-tab-badge {
  background: rgba(var(--colors-amber-900), 1);
  color: rgba(var(--colors-amber-200), 1);
}

/* Tree icons */
.tree-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.5rem;
  font-size: 0.65rem;
  font-weight: 700;
  flex-shrink: 0;
}

.tree-icon-course {
  background: rgba(var(--colors-primary-100), 1);
  color: rgba(var(--colors-primary-700), 1);
}

.tree-icon-chapter {
  background: rgba(var(--colors-gray-100), 1);
  color: rgba(var(--colors-gray-600), 1);
}

.tree-icon-lesson {
  width: 1.5rem;
  height: 1.5rem;
  font-size: 0.6rem;
  background: rgba(var(--colors-gray-50), 1);
  color: rgba(var(--colors-gray-500), 1);
  border: 1px solid rgba(var(--colors-gray-200), 1);
}

/* Lesson tree — indent only, no connector lines */
.tree-lessons {
  padding: 0.25rem 0 0.5rem;
  border-right: 2px solid rgba(var(--colors-gray-200), 1);
  margin-right: 1.25rem;
}

.tree-lesson-item {
  padding-right: 0.5rem;
}

.tree-lesson-row {
  padding: 0 0.25rem;
}

.tree-lesson-detail {
  position: relative;
}

.tree-lessons-empty {
  padding-right: 1.5rem;
}

html.dark .tree-lessons,
.dark .tree-lessons {
  border-color: rgba(var(--colors-gray-600), 1);
}

html.dark .tree-icon-chapter,
.dark .tree-icon-chapter {
  background: rgba(var(--colors-gray-700), 1);
  color: rgba(var(--colors-gray-300), 1);
}

html.dark .tree-icon-lesson,
.dark .tree-icon-lesson {
  background: rgba(var(--colors-gray-800), 1);
  border-color: rgba(var(--colors-gray-600), 1);
  color: rgba(var(--colors-gray-400), 1);
}
</style>

<style>
.modal[data-modal-open="true"]:has(.approval-modal-shell) + [dusk="modal-backdrop"] {
  animation: approval-backdrop-in 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes approval-backdrop-in {
  from {
    opacity: 0;
  }
}
</style>
